import {
  getTenantSlugHostErrorCode,
  TENANT_SLUG_CHANGE_COOLDOWN_DAYS,
  TENANT_SLUG_QUARANTINE_DAYS,
} from "@repo/shared";
import type { TenantRenameErrorCode } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiErrorCode } from "@/lib/api/_client";
import { authClient } from "@/lib/auth-client";
import { useUpdateTenantOrganization } from "@/lib/hooks/use-tenant-organization";
import { useT } from "@/lib/i18n/use-t";
import { rememberOrgSlug } from "@/lib/remembered-org";
import { getTenantHostContext, tenantUrl } from "@/lib/tenant-host";
import { T } from "./change-slug-dialog.i18n";

type ChangeSlugDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSlug: string;
  /** Tenant subdomain root, or `null` when the app runs on a single origin. */
  rootDomain: string | null;
};

const withRoot = (slug: string, rootDomain: string | null) =>
  rootDomain ? `${slug}.${rootDomain}` : slug;

/**
 * QL-358 — the organization address change: live format check, a summary of the
 * effects (QL-351 redirect and reservation), and confirmation by retyping the new
 * address. The server stays authoritative for taken, quarantined and cooldown.
 */
export function ChangeSlugDialog({
  open,
  onOpenChange,
  currentSlug,
  rootDomain,
}: ChangeSlugDialogProps) {
  const t = useT(T);
  const updateOrganization = useUpdateTenantOrganization();
  const [slug, setSlug] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSlug("");
      setConfirmation("");
      setError(null);
    }
  }, [open]);

  const normalized = slug.trim().toLowerCase();
  const formatCode = normalized ? getTenantSlugHostErrorCode(normalized) : null;
  const canSubmit =
    normalized !== "" &&
    normalized !== currentSlug &&
    formatCode === null &&
    confirmation.trim().toLowerCase() === normalized &&
    !updateOrganization.isPending &&
    !leaving;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    updateOrganization.mutate(
      { slug: normalized },
      {
        onSuccess: async (organization) => {
          setLeaving(true);
          rememberOrgSlug(organization.slug);
          // The API moved every session to the new slug; skip this browser's
          // cookie cache so the new host does not see the old one for a minute.
          await authClient.getSession({ query: { disableCookieCache: true } }).catch(() => {});
          if (getTenantHostContext().kind === "tenant") {
            window.location.replace(tenantUrl(organization.slug, "/settings/organization"));
          } else {
            window.location.reload();
          }
        },
        onError: (err: unknown) => {
          const code = apiErrorCode(err) as TenantRenameErrorCode | undefined;
          setError(
            code && code in t.errors
              ? t.errors[code]
              : err instanceof Error && err.message
                ? err.message
                : t.failed,
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !leaving && onOpenChange(next)}>
      <DialogScrollContent className="max-w-lg">
        <DialogScrollHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="new-slug">{t.newAddress}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="new-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  placeholder={currentSlug}
                  autoComplete="off"
                  autoFocus
                  disabled={updateOrganization.isPending || leaving}
                  aria-invalid={formatCode !== null}
                />
                {rootDomain && (
                  <span className="shrink-0 text-sm text-muted-foreground">.{rootDomain}</span>
                )}
              </div>
              <p
                className={
                  formatCode ? "text-xs text-destructive" : "text-xs text-muted-foreground"
                }
              >
                {formatCode ? t.errors[formatCode] : t.slugHelp}
              </p>
            </div>

            <div className="rounded-md border bg-muted/50 p-3 text-sm space-y-1">
              <p className="font-medium">{t.whatHappens}</p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                <li>
                  {t.effectRedirect(
                    withRoot(currentSlug, rootDomain),
                    String(TENANT_SLUG_QUARANTINE_DAYS),
                  )}
                </li>
                <li>{t.effectSso(String(TENANT_SLUG_QUARANTINE_DAYS))}</li>
                <li>{t.effectUsers}</li>
                <li>{t.effectIntegrations}</li>
                <li>{t.effectCooldown(String(TENANT_SLUG_CHANGE_COOLDOWN_DAYS))}</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-slug">{t.confirm}</Label>
              <Input
                id="confirm-slug"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder={normalized || undefined}
                autoComplete="off"
                disabled={updateOrganization.isPending || leaving}
              />
            </div>
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={updateOrganization.isPending || leaving}>
              {t.cancel}
            </DialogCancel>
            <Button
              type="submit"
              disabled={!canSubmit}
              loading={updateOrganization.isPending || leaving}
            >
              {t.submit}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
